"use client";

import { motion } from "framer-motion";
import Navbar from "@/components/navbar";
import Link from "next/link";
import { useTranslations } from "next-intl";

const INDUSTRIES = ["Medical Clinics", "Real Estate", "Law Firms", "Home Services", "Salons"];

function WaveformBar({ index }) {
  const heights = [8, 14, 22, 30, 40, 30, 22, 14];
  const base = heights[index % heights.length];
  return (
    <motion.div
      className="w-[3px] rounded-full bg-gradient-to-t from-violet-500 to-cyan-400 flex-shrink-0"
      animate={{ height: [`${base}px`, `${base * 1.9}px`, `${base}px`] }}
      transition={{
        duration: 0.65 + index * 0.025,
        repeat: Infinity,
        repeatType: "mirror",
        ease: "easeInOut",
        delay: index * 0.035,
      }}
      style={{ height: `${base}px` }}
    />
  );
}

function PhoneCallMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 bg-violet-500/25 blur-3xl rounded-full scale-75 pointer-events-none" />

      {/* Phone frame */}
      <div className="relative w-64 bg-zinc-900 rounded-[40px] p-2.5 border border-white/10 shadow-2xl shadow-violet-500/20">
        <div className="bg-zinc-950 rounded-[32px] overflow-hidden">

          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 pb-1">
            <span className="text-[10px] text-zinc-500 font-medium tabular-nums">9:41</span>
            <div className="flex items-center gap-1">
              <div className="flex gap-px items-end">
                {[3, 5, 7, 9].map((h, i) => (
                  <div key={i} className="w-[3px] bg-zinc-500 rounded-sm" style={{ height: `${h}px` }} />
                ))}
              </div>
              <div className="w-4 h-2.5 border border-zinc-500 rounded-sm ml-1 relative">
                <div className="absolute inset-[2px] right-[4px] bg-zinc-500 rounded-[1px]" />
                <div className="absolute right-[-3px] top-[3px] w-[2px] h-[4px] bg-zinc-500 rounded-r-sm" />
              </div>
            </div>
          </div>

          {/* Call screen */}
          <div className="px-5 py-5 flex flex-col items-center gap-4">

            {/* Avatar with pulse rings */}
            <div className="relative flex items-center justify-center">
              <motion.div
                className="absolute w-24 h-24 rounded-full bg-violet-500/15"
                animate={{ scale: [1, 1.5, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              />
              <motion.div
                className="absolute w-20 h-20 rounded-full bg-violet-500/20"
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: 0.4 }}
              />
              <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-violet-500/40 z-10">
                AI
              </div>
            </div>

            <div className="text-center">
              <div className="text-white font-semibold text-sm">Asynq Voice Agent</div>
              <div className="flex items-center gap-1.5 justify-center mt-1">
                <motion.div
                  className="w-1.5 h-1.5 rounded-full bg-green-400"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
                <span className="text-green-400 text-[11px] font-medium tabular-nums">Connected · 0:47</span>
              </div>
            </div>

            {/* Waveform */}
            <div className="flex items-center justify-center gap-0.5 h-12 w-full px-1">
              {Array.from({ length: 30 }).map((_, i) => (
                <WaveformBar key={i} index={i} />
              ))}
            </div>

            {/* Live transcript */}
            <div className="w-full bg-zinc-900/80 rounded-xl p-3 border border-white/5 space-y-2">
              <div className="flex gap-2 items-start">
                <span className="text-zinc-500 text-[10px] mt-0.5 shrink-0 font-medium">Caller</span>
                <span className="text-zinc-300 text-[10px] leading-relaxed">I&apos;d like to book for tomorrow at noon</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-violet-400 text-[10px] mt-0.5 shrink-0 font-medium">Agent</span>
                <span className="text-zinc-300 text-[10px] leading-relaxed">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 0.4 }}
                  >
                    Noon is open — shall I confirm?
                  </motion.span>
                </span>
              </div>
            </div>

            {/* Call controls */}
            <div className="flex items-center gap-5 pt-1">
              <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/5 flex items-center justify-center">
                <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057.101 18.08.114 18.1.134 18.112a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
              </div>
              <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/5 flex items-center justify-center">
                <svg className="w-4 h-4 text-zinc-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 14H6v-2h2v2zm0-4H6V8h2v2zm4 4h-2v-2h2v2zm0-4h-2V8h2v2zm4 4h-2v-2h2v2zm0-4h-2V8h2v2z"/>
                </svg>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Floating stat: 100% answered */}
      <motion.div
        animate={{ y: [0, -7, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-6 top-8 bg-zinc-900/90 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 shadow-xl shadow-black/40"
      >
        <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent leading-none">100%</div>
        <div className="text-zinc-500 text-[11px] mt-1">Calls answered</div>
      </motion.div>

      {/* Floating stat: Instant */}
      <motion.div
        animate={{ y: [0, 7, 0] }}
        transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-6 bottom-16 bg-zinc-900/90 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 shadow-xl shadow-black/40"
      >
        <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent leading-none">&lt;1s</div>
        <div className="text-zinc-500 text-[11px] mt-1">Response time</div>
      </motion.div>

      {/* Floating stat: 24/7 */}
      <motion.div
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute -right-4 bottom-10 bg-zinc-900/90 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 shadow-xl shadow-black/40"
      >
        <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent leading-none">24/7</div>
        <div className="text-zinc-500 text-[11px] mt-1">Always on</div>
      </motion.div>

    </motion.div>
  );
}

export default function VoiceAgentHero({ title, subtitle }) {
  const t = useTranslations("common");

  return (
    <div className="relative min-h-[95vh] flex flex-col overflow-hidden bg-zinc-950">
      <Navbar />

      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_90%_at_90%_50%,rgba(124,58,237,0.13),transparent_65%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_70%_at_10%_90%,rgba(6,182,212,0.07),transparent_65%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_30%_40%_at_50%_0%,rgba(124,58,237,0.06),transparent_70%)] pointer-events-none" />
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Top badges */}
              <div className="flex items-center gap-3 mb-8 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium">
                  <motion.span
                    className="w-2 h-2 rounded-full bg-green-400"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  Live — 24/7 coverage
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  Voice AI
                </div>
              </div>

              <h1 className="text-5xl lg:text-6xl xl:text-[4.25rem] font-bold text-white mb-5 leading-[1.08] tracking-tight">
                {title}
              </h1>

              {/* ROI hook line */}
              <p className="text-base text-violet-300/80 font-medium mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-violet-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                Businesses capture 3× more inbound leads — with zero extra headcount
              </p>

              <p className="text-xl text-zinc-400 leading-relaxed mb-10 max-w-lg">
                {subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/contact">
                  <button className="px-8 py-4 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl font-semibold text-base hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-violet-500/30">
                    {t("cta.bookCall")}
                  </button>
                </Link>
                <Link href="/portfolio">
                  <button className="px-8 py-4 border border-white/10 text-zinc-300 rounded-xl font-medium text-base hover:border-white/25 hover:text-white hover:bg-white/5 transition-all">
                    See case studies →
                  </button>
                </Link>
              </div>

              {/* Social proof strip */}
              <div className="mt-12 pt-8 border-t border-white/[0.07]">
                <p className="text-xs text-zinc-600 uppercase tracking-widest font-medium mb-3">Deployed for</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {INDUSTRIES.map((industry) => (
                    <span
                      key={industry}
                      className="text-sm font-medium text-zinc-400 bg-white/[0.04] hover:bg-white/[0.07] px-3 py-1.5 rounded-full border border-white/[0.07] transition-colors"
                    >
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Phone mockup */}
            <div className="flex justify-center lg:justify-end lg:pr-6">
              <PhoneCallMockup />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
